import { getCurrencySymbol } from '@/Components/Admin/Projects/PerformanceMarketing/PerformanceMarketingUtils';
import { contentAnalysisStatuses, entityEffectiveStatusTypes } from '@/Components/Admin/Projects/PerformanceMarketing/PerformanceMarketingConstants';
import Formatter from '@/Utils/Formatter';
import { convertToMajorValue } from '@/Utils/NumberUtility';

const createNotificationItem = ({ isEnabled, key, storageKey, type, message, canClose, action = null, canDismiss = true }) => {
    const item = { isEnabled, key, storageKey, type, message, canClose, canDismiss };
    if (action) {
        item.action = action;
    }
    return item;
};

const isCampaignCompleted = (campaign) => campaign && campaign.effectiveStatus === entityEffectiveStatusTypes.completed;
const formatCurrency = (value, currencySymbol = '€') => Formatter.format(convertToMajorValue(value), 'C2', currencySymbol);
const campaignSuffix = (campaign) => campaign && campaign.id ? `:${campaign.id}` : '';
export function buildNotificationItemCompletedCampaignFunds(campaign) {
    const item = createNotificationItem({
        isEnabled: isCampaignCompleted(campaign) && campaign.fundsRemaining > 0,
        key: `completedCampaignFunds${campaignSuffix(campaign)}`,
        storageKey: `performanceMarketingCompletedCampaignFundsNotification${campaignSuffix(campaign)}`,
        type: 'info',
        message: `Your campaign is complete. Final statistics might still change due to the 7-day attribution window.
                  Remaining funds will be available shortly for new and active campaigns.`,
        canClose: true,
    });
    item.qa = 'CompletedCampaignFunds';
    return item;
}

export function buildNotificationItemUnusedFunds(wallets, campaign, isCampaignRelated = false) {
    const balancesWithUnusedFunds = wallets.filter(b => b.availableFunds > 0);
    const formattedBalances = balancesWithUnusedFunds.map(b => formatCurrency(b.availableFunds, getCurrencySymbol(b.currencyShortName)));
    const balancesLabel = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(formattedBalances);

    return createNotificationItem({
        isEnabled: balancesWithUnusedFunds.length > 0 && (isCampaignCompleted(campaign) || !isCampaignRelated),
        key: `unusedFunds${campaignSuffix(campaign)}`,
        storageKey: `performanceMarketingUnusedFundsAvailabilityNotification${campaignSuffix(campaign)}`,
        type: 'info',
        message: `You have ${balancesLabel} in your business balance. You can
        assign them to a new or existing campaign anytime from the campaign's dashboard or during setup.`,
        canClose: true,
    });
}

export function buildNotificationItemAssignUnusedFunds(campaign, unusedFunds, assignUnusedFunds, canDismiss = true, currencySymbol = '€') {
    return createNotificationItem({
        isEnabled: !isCampaignCompleted(campaign) && unusedFunds > 0,
        key: `assignUnusedFunds${campaignSuffix(campaign)}`,
        storageKey: `performanceMarketingAssignUnusedFundsNotification${campaignSuffix(campaign)}${canDismiss ? '' : 'NoDismiss'}`,
        type: 'info',
        message: `You have ${formatCurrency(unusedFunds, currencySymbol)} in your business balance. You can assign them to this
        campaign and use them for your current marketing.`,
        canClose: false,
        action: { label: 'Assign Funds', onClick: assignUnusedFunds },
        canDismiss,
    });
}

export function buildNotificationItemAnalysisInProgress(campaign) {
    return createNotificationItem({
        isEnabled: campaign && campaign.contentAnalysisStatus === contentAnalysisStatuses.pending,
        key: `analysisInProgress${campaignSuffix(campaign)}`,
        storageKey: `performanceMarketingAnalysisInProgressNotification${campaignSuffix(campaign)}`,
        type: 'info',
        message: `AI analysis of your project is in progress. We’re reviewing your project to generate helpful
              suggestions for ad assets and ensure top performance.`,
        canClose: true,
    });
}

export function buildNotificationItemAnalysisFailed(campaign) {
    return createNotificationItem({
        isEnabled: campaign && campaign.contentAnalysisStatus === contentAnalysisStatuses.failed,
        key: `analysisFailed${campaignSuffix(campaign)}`,
        storageKey: `performanceMarketingAnalysisFailedNotification${campaignSuffix(campaign)}`,
        type: 'warning',
        message: `Something went wrong with the AI analysis of your project. We’re working on resolving the issue
              so we can generate helpful suggestions. You can still prepare your ads manually.`,
        canClose: true,
    });
}

export function buildNotificationItemTopUpRecommendation(campaign, threeDaysSpending, currencySymbol) {
    return createNotificationItem({
        isEnabled: campaign && campaign.totalFunds === 0,
        key: `topUpRecommendation${campaignSuffix(campaign)}`,
        storageKey: `performanceMarketingTopUpRecommendationNotification${campaignSuffix(campaign)}`,
        type: 'info',
        message: `We recommend topping up your budget for the first few days of your campaign after its creation
                  and before it starts. Estimated amount for the first 3 days of ad emission: ${formatCurrency(threeDaysSpending, currencySymbol)}`,
        canClose: false,
    });
}

export function buildNotificationItemSynchronizingResults(campaign) {
    const item = createNotificationItem({
        isEnabled: campaign.effectiveStatus === entityEffectiveStatusTypes.completed && !campaign.isProcessingFinalized,
        key: `SynchronizingResults${campaignSuffix(campaign)}`,
        storageKey: `performanceMarketingSynchronizingResultsNotification${campaignSuffix(campaign)}`,
        type: 'info',
        message: 'Marketing results are still being synchronised. A fee invoice for this campaign will be issued after the final statistics have been determined.',
        canClose: false,
    });
    item.qa = 'SynchronizingResults';
    return item;
}

export function buildNotificationItemOverdueInvoice(hasUnpaidInvoices, goToInvoices) {
    return createNotificationItem({
        isEnabled: hasUnpaidInvoices,
        key: 'overdueInvoice',
        storageKey: 'performanceMarketingOverdueInvoiceNotification',
        type: 'warning',
        message: 'You have unpaid invoices for your marketing. You can view, download, and pay them directly from the Invoices section.',
        canClose: false,
        canDismiss: false,
        action: { label: 'Go to invoices', onClick: goToInvoices },
    });
}

export const buildNotificationItem = {
    completedCampaignFunds: buildNotificationItemCompletedCampaignFunds,
    unusedFunds: buildNotificationItemUnusedFunds,
    assignUnusedFunds: buildNotificationItemAssignUnusedFunds,
    analysisInProgress: buildNotificationItemAnalysisInProgress,
    analysisFailed: buildNotificationItemAnalysisFailed,
    topUpRecommendation: buildNotificationItemTopUpRecommendation,
    synchronizingResults: buildNotificationItemSynchronizingResults,
    overdueInvoice: buildNotificationItemOverdueInvoice,
};
